import NumberInput from '@lyra/core/components/Input/NumberInput'
import Notice from '@lyra/core/components/Notice'
import Section from '@lyra/core/components/Section'
import VideoText from '@lyra/core/components/VideoText'
import formatUSD from '@lyra/core/utils/formatUSD'
import { Ticker } from '@lyra/web/constants/instruments'
import { OrderQuoteParams, SetOrderQuoteParamsForInstrument } from '@lyra/web/constants/order'
import {
  getAutoLimitPrice,
  getIsLimitPriceOutOfBounds,
  getValidLimitPrice,
} from '@lyra/web/utils/order'
import { useCallback, useState } from 'react'

import { countDecimals } from '../../../utils/number'

type Props = {
  ticker: Ticker
  quoteParams: OrderQuoteParams
  onChangeQuoteParams: SetOrderQuoteParamsForInstrument
}

const TradeFormLimitPriceInput = ({ ticker, quoteParams, onChangeQuoteParams }: Props) => {
  const tickSize = +ticker.tick_size
  const priceDps = countDecimals(tickSize)

  const limitPrice = getValidLimitPrice(+quoteParams.limit_price, quoteParams, ticker)
  const autoLimitPrice = getAutoLimitPrice(quoteParams, ticker)

  const [rawLimitPrice, setRawLimitPrice] = useState(limitPrice)
  const [isFocused, setIsFocused] = useState(false)
  const [showOutOfBoundsWarning, setShowOutOfBoundsWarning] = useState(false)

  const minPrice = +ticker.min_price
  const maxPrice = +ticker.max_price

  const isBuy = quoteParams.direction === 'buy'

  const handleSetLimitPrice = useCallback(
    (limitPriceInput: number) => {
      setRawLimitPrice(limitPriceInput)
      setShowOutOfBoundsWarning(
        limitPriceInput !== 0 && getIsLimitPriceOutOfBounds(limitPriceInput, quoteParams, ticker)
      )
      const validLimitPrice = getValidLimitPrice(limitPriceInput, quoteParams, ticker)
      onChangeQuoteParams((params) => ({
        ...params,
        limit_price: validLimitPrice.toString(),
      }))
    },
    [onChangeQuoteParams, quoteParams, ticker]
  )

  const isAutoLimitPrice = limitPrice === autoLimitPrice

  return (
    <Section.YStack>
      <NumberInput
        width="100%"
        value={isFocused ? rawLimitPrice : limitPrice}
        renderAsPlaceholder={isAutoLimitPrice}
        formatValue={(val) => formatUSD(val, { dps: priceDps })}
        onChangeValue={handleSetLimitPrice}
        onFocus={() => {
          setShowOutOfBoundsWarning(false)
          setIsFocused(true)
        }}
        onBlur={() => setIsFocused(false)}
        leftContent={<VideoText color="secondary">Price</VideoText>}
        textAlign="right"
      />
      {showOutOfBoundsWarning && !isFocused ? (
        <Notice
          status="warning"
          message={`Price must be ${isBuy ? 'less' : 'greater'} than ${formatUSD(
            isBuy ? maxPrice : minPrice,
            {
              dps: priceDps,
            }
          )}`}
          width="100%"
          onClose={() => setShowOutOfBoundsWarning(false)}
        />
      ) : null}
    </Section.YStack>
  )
}

export default TradeFormLimitPriceInput
